import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, ScanText } from 'lucide-react';
import { api } from '@/lib/api';
import {
  CampoVivienda, ETIQUETA_VIVIENDA, FilaProcesado, PropuestaProcesado, ResultadoProcesado,
} from '@/lib/contratos';

/**
 * «Procesar contrato»: lo que el propietario rellenó al firmar en Click & Sign,
 * frente a lo que tiene la ficha. Lo que rellena un hueco viene marcado; lo
 * que pisaría un dato distinto, no. No se guarda nada hasta pulsar Guardar, y
 * el servidor vuelve a leer el PDF al guardar.
 */

const GRUPOS: Array<{ grupo: FilaProcesado['grupo']; titulo: string }> = [
  { grupo: 'titular', titulo: 'Titular' },
  { grupo: 'segundo', titulo: 'Segundo titular' },
  { grupo: 'condiciones', titulo: 'Condiciones del contrato' },
];

const CAMPOS_VIVIENDA: CampoVivienda[] = ['codigo', 'catastro', 'nra', 'garaje'];

const comparable = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9@]/g, '');

function Fila({ etiqueta, nuevo, actual, aviso, marcado, onCambio }: {
  etiqueta: string;
  nuevo: string;
  actual: string;
  aviso?: string;
  marcado: boolean;
  onCambio: (marcado: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <input type="checkbox" className="mt-1 w-4 h-4 rounded shrink-0" checked={marcado} onChange={e => onCambio(e.target.checked)} />
      <span className="min-w-0 flex-1 grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-x-3 text-sm">
        <span className="text-sereno-dark/50">{etiqueta}</span>
        <span className="min-w-0">
          <span className="break-words">{nuevo}</span>
          {actual && <span className="block text-xs text-sereno-dark/40 break-words">En la ficha: {actual}</span>}
          {aviso && (
            <span className="flex items-start gap-1 text-xs text-amber-700 mt-0.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {aviso}
            </span>
          )}
        </span>
      </span>
    </label>
  );
}

export default function ProcesarContrato({ signatureId, onCerrar, onGuardado, onVerFicha }: {
  signatureId: string;
  onCerrar: () => void;
  onGuardado: () => void;
  onVerFicha: () => void;
}) {
  const [propuesta, setPropuesta] = useState<PropuestaProcesado | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [destinos, setDestinos] = useState<Record<string, string | null>>({});
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoProcesado | null>(null);

  const actualDe = (p: PropuestaProcesado, propertyId: string | null, campo: CampoVivienda) =>
    p.propiedades.find(x => x.id === propertyId)?.actuales[campo] || '';

  /** Marcados de entrada de una vivienda: lo que la vivienda elegida no tiene. */
  const marcadosVivienda = (p: PropuestaProcesado, numero: number, propertyId: string | null) => {
    const v = p.viviendas.find(x => x.numero === numero)!;
    return CAMPOS_VIVIENDA.filter(c => v.datos[c] && propertyId && !actualDe(p, propertyId, c)).map(c => `vivienda${numero}.${c}`);
  };

  useEffect(() => {
    let vigente = true;
    api.proponerProcesadoContrato(signatureId)
      .then(p => {
        if (!vigente) return;
        setPropuesta(p);
        setDestinos(Object.fromEntries(p.viviendas.map(v => [String(v.numero), v.propertyId])));
        setMarcados(new Set([
          ...p.filas.filter(f => f.marcar).map(f => f.clave),
          ...p.viviendas.flatMap(v => marcadosVivienda(p, v.numero, v.propertyId)),
        ]));
      })
      .catch(err => { if (vigente) setError(err instanceof Error ? err.message : 'No se pudo leer el contrato'); });
    return () => { vigente = false; };
  }, [signatureId]);

  const iguales = useMemo(() => {
    if (!propuesta) return [];
    const lista = propuesta.filas.filter(f => f.estado === 'igual').map(f => f.grupo === 'segundo' ? `${f.etiqueta} (2.º titular)` : f.etiqueta);
    for (const v of propuesta.viviendas) {
      for (const c of CAMPOS_VIVIENDA) {
        const valor = v.datos[c];
        if (valor && destinos[String(v.numero)] && comparable(valor) === comparable(actualDe(propuesta, destinos[String(v.numero)], c))) {
          lista.push(`${ETIQUETA_VIVIENDA[c]} (vivienda ${v.numero})`);
        }
      }
    }
    return lista;
  }, [propuesta, destinos]);

  const marcar = (clave: string, si: boolean) => setMarcados(m => {
    const n = new Set(m);
    if (si) n.add(clave); else n.delete(clave);
    return n;
  });

  if (error && !propuesta) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">{error}</p>
        <div className="flex justify-end"><button type="button" onClick={onCerrar} className="btn-secondary text-sm">Cerrar</button></div>
      </div>
    );
  }

  if (!propuesta) {
    return (
      <div className="py-10 flex flex-col items-center gap-2 text-sm text-sereno-dark/60">
        <ScanText size={20} className="text-sereno-dark/40 animate-pulse" />
        Leyendo el contrato firmado…
      </div>
    );
  }

  if (resultado) {
    const nada = resultado.guardados.length === 0 && !resultado.pdfArchivado;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm">
          <Check size={16} className="text-green-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{nada ? 'No había nada que guardar.' : 'Guardado en la ficha.'}</p>
            {resultado.guardados.length > 0 && <p className="text-sereno-dark/60 mt-1">{resultado.guardados.join(' · ')}</p>}
            {resultado.pdfArchivado && <p className="text-sereno-dark/60 mt-1">El PDF firmado está ahora en Documentos de contrato.</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="btn-ghost border border-gray-200 text-sm">Cerrar</button>
          <button type="button" onClick={onVerFicha} className="btn-primary text-sm">Ver la ficha</button>
        </div>
      </div>
    );
  }

  const p = propuesta;
  const pendientes = p.filas.filter(f => f.estado !== 'igual');
  const viviendas = p.viviendas.map(v => {
    const destino = destinos[String(v.numero)] ?? null;
    const campos = CAMPOS_VIVIENDA.filter(c => v.datos[c] && !(destino && comparable(v.datos[c]!) === comparable(actualDe(p, destino, c))));
    return { v, destino, campos };
  }).filter(x => x.campos.length > 0);
  const claves = [...marcados].filter(k => {
    const m = k.match(/^vivienda(\d+)\.(\w+)$/);
    if (!m) return pendientes.some(f => f.clave === k);
    const x = viviendas.find(y => y.v.numero === Number(m[1]));
    return !!x?.destino && x.campos.includes(m[2] as CampoVivienda);
  });
  const nadaQueHacer = pendientes.length === 0 && viviendas.length === 0;

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      setResultado(await api.procesarContrato(signatureId, { claves, viviendas: destinos }));
      onGuardado();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-sereno-dark/50">
        Lo que se rellenó al firmar <span className="font-mono">{p.codigo}</span>. Viene marcado lo que la ficha no tiene;
        lo que ya tiene con otro valor no se cambia si no lo marcas.
        {p.origen === 'word' && ' Es un contrato de las plantillas antiguas: cada dato se reconoce por el texto que tiene al lado, así que conviene echarle un ojo.'}
      </p>

      {nadaQueHacer && (
        <p className="text-sm text-sereno-dark/70">La ficha ya tiene todo lo que trae este contrato.</p>
      )}

      {GRUPOS.map(({ grupo, titulo }) => {
        const filas = pendientes.filter(f => f.grupo === grupo);
        if (!filas.length) return null;
        return (
          <div key={grupo}>
            <h4 className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider mb-1">{titulo}</h4>
            <div className="divide-y divide-gray-100">
              {filas.map(f => (
                <Fila key={f.clave} etiqueta={f.etiqueta} nuevo={f.nuevo} actual={f.actual} aviso={f.aviso}
                  marcado={marcados.has(f.clave)} onCambio={si => marcar(f.clave, si)} />
              ))}
            </div>
          </div>
        );
      })}

      {viviendas.map(({ v, destino, campos }) => (
        <div key={v.numero}>
          <h4 className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider mb-1">
            Vivienda {p.viviendas.length > 1 ? `(${v.numero})` : ''}
          </h4>
          {v.direccion && <p className="text-sm text-sereno-dark/70 mb-2">{v.direccion}</p>}
          {p.propiedades.length === 0 ? (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 mb-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              Este propietario aún no tiene viviendas en el CRM. Asígnale la suya en Propiedades y vuelve a procesar el contrato para guardar estos códigos.
            </p>
          ) : (
            <div className="mb-1">
              <label className="label">Guardar en</label>
              <select
                className="input text-sm"
                value={destino || ''}
                onChange={e => {
                  const nuevo = e.target.value || null;
                  setDestinos(d => ({ ...d, [String(v.numero)]: nuevo }));
                  setMarcados(m => {
                    const n = new Set([...m].filter(k => !k.startsWith(`vivienda${v.numero}.`)));
                    for (const k of marcadosVivienda(p, v.numero, nuevo)) n.add(k);
                    return n;
                  });
                }}
              >
                <option value="">— Elige la vivienda —</option>
                {p.propiedades.map(x => <option key={x.id} value={x.id}>{x.nombre}{x.direccion ? ` · ${x.direccion}` : ''}</option>)}
              </select>
            </div>
          )}
          <div className="divide-y divide-gray-100">
            {campos.map(c => (
              destino ? (
                <Fila key={c} etiqueta={ETIQUETA_VIVIENDA[c]} nuevo={v.datos[c]!} actual={actualDe(p, destino, c)}
                  marcado={marcados.has(`vivienda${v.numero}.${c}`)} onCambio={si => marcar(`vivienda${v.numero}.${c}`, si)} />
              ) : (
                <div key={c} className="py-2 pl-7 grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-x-3 text-sm">
                  <span className="text-sereno-dark/50">{ETIQUETA_VIVIENDA[c]}</span>
                  <span className="break-words text-sereno-dark/70">{v.datos[c]}</span>
                </div>
              )
            ))}
          </div>
        </div>
      ))}

      {iguales.length > 0 && (
        <p className="text-xs text-sereno-dark/40 flex items-start gap-1.5">
          <Check size={12} className="mt-0.5 shrink-0 text-green-600" />
          <span>Ya coinciden con la ficha: {iguales.join(', ')}.</span>
        </p>
      )}

      {p.archivarPdf && (
        <p className="text-xs text-sereno-dark/50">Al guardar, el PDF firmado se añade también a Documentos de contrato.</p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCerrar} disabled={guardando} className="btn-ghost border border-gray-200 text-sm">Cancelar</button>
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={guardando || (claves.length === 0 && !p.archivarPdf)}
          className="btn-primary text-sm flex items-center gap-2"
        >
          {guardando && <Loader2 size={14} className="animate-spin" />}
          {claves.length > 0 ? `Guardar en la ficha (${claves.length})` : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
